import React, { useEffect, useRef, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import PropTypes from 'prop-types'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

import UploadFileIcon from '@mui/icons-material/UploadFile'

import axios from '../../dataClient/dataClient'
import { redactionFileTypes } from '../../util/categoryConsts'
import { BoldFormTitle, ErrorText, Subheading } from '../styles/Text'
import { FlexEndContainer, PaddedForm, StyledDialog, StyledDialogContent, FlexColumn, MedBGContainer, WhiteBGContainer } from '../styles/Containers'
import { ErrorMessage, InfoMessage, SuccessMessage } from '../common/UserMessage'
import Button, { SecondaryButton, SecondaryIconButton } from '../common/Button'
import UploadFile from '../files/UploadFile'
import BrowserFile from '../files/BrowserFile'
import DatabaseFile from '../files/DatabaseFile'

const schema = yup.object().shape({
  file: yup.mixed().required('A replacement file is required')
})

function ReplaceRedactionFileButton({ categoryId, existingFile, disabled = false, onSuccess = () => {} }) {
  const [isVisible, setIsVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const [display, setDisplay] = useState('form')

  const methods = useForm({ resolver: yupResolver(schema) })
  const { handleSubmit, watch, formState, setValue, reset } = methods

  const file = watch('file')

  const isMounted = useRef(true)

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  const handleOpen = () => setIsVisible(true)
  function handleClose() {
    setIsVisible(false)
    setIsLoading(false)
    setError(false)
    reset()
    setDisplay('form')
  }

  const handleUploadFiles = acceptedFiles => setValue('file', acceptedFiles[0])
  const handleDelete = () => setValue('file', undefined)

  const onError = (errors, e) => console.error('Error', errors, e)

  async function onSubmit(data) {
    setIsLoading(true)
    setError(false)

    const formData = new FormData()
    formData.append('file', data.file)
    formData.append('fileId', existingFile.id)

    const config = { headers: { 'Content-Type': 'multipart/form-data' } }
    axios
      .post(`/category/${categoryId}/replaceFile`, formData, config)
      .then(() => {
        setDisplay('success')
        onSuccess(categoryId)
      })
      .catch(e => {
        setError(true)
        console.error(`failed to replace file ${existingFile.id} for category with id`, categoryId, e)
      })
      .finally(() => setIsLoading(false))
  }

  const fileError = !!formState.errors.file
  const fileHelperText = formState.errors?.file?.message

  return (
    <>
      <SecondaryIconButton onClick={handleOpen} disabled={disabled} aria-label="Replace File">
        <UploadFileIcon />
      </SecondaryIconButton>
      <StyledDialog maxwidth="800px" open={isVisible} onClose={handleClose} keepMounted={false}>
        <StyledDialogContent>
          {display === 'form' && (
            <FormProvider {...methods}>
              <PaddedForm onSubmit={handleSubmit(onSubmit, onError)}>
                <BoldFormTitle>Replace File</BoldFormTitle>
                <FlexColumn gap="10px">
                  <Subheading>Existing File</Subheading>
                  <MedBGContainer padding="5px 20px 5px 5px">
                    <DatabaseFile file={existingFile} buttonAction="download" spacing="medium" />
                  </MedBGContainer>
                </FlexColumn>
                <FlexColumn gap="10px">
                  <Subheading>New File</Subheading>
                  <InfoMessage>Uploading a new file will replace the existing file.</InfoMessage>
                  {file ? (
                    <MedBGContainer padding="5px 20px 5px 5px">
                      <BrowserFile file={file} onDelete={handleDelete} loading={isLoading} />
                    </MedBGContainer>
                  ) : (
                    <FlexColumn gap="5px">
                      <UploadFile onUpload={handleUploadFiles} maxFiles={1} />
                      {fileError && <ErrorText role="alert">{fileHelperText}</ErrorText>}
                    </FlexColumn>
                  )}
                </FlexColumn>
                {error && <ErrorMessage>There was an error replacing the file. Please try again.</ErrorMessage>}
                <FlexEndContainer>
                  <SecondaryButton onClick={handleClose} disabled={isLoading}>
                    Cancel
                  </SecondaryButton>
                  <Button type="submit" loading={isLoading}>
                    Confirm & Replace File
                  </Button>
                </FlexEndContainer>
              </PaddedForm>
            </FormProvider>
          )}
          {display === 'success' && (
            <WhiteBGContainer padding="30px">
              <FlexColumn>
                <BoldFormTitle>Replace File</BoldFormTitle>
                <SuccessMessage>File successfully replaced.</SuccessMessage>
                <FlexEndContainer>
                  <SecondaryButton onClick={handleClose}>Close</SecondaryButton>
                </FlexEndContainer>
              </FlexColumn>
            </WhiteBGContainer>
          )}
        </StyledDialogContent>
      </StyledDialog>
    </>
  )
}

ReplaceRedactionFileButton.propTypes = {
  categoryId: PropTypes.string.isRequired,
  existingFile: PropTypes.shape({
    id: PropTypes.string.isRequired,
    filename: PropTypes.string.isRequired,
    usage: PropTypes.string.isRequired,
    status: PropTypes.oneOf(redactionFileTypes).isRequired,
    size: PropTypes.number.isRequired
  }).isRequired,
  disabled: PropTypes.bool,
  onSuccess: PropTypes.func
}

export default ReplaceRedactionFileButton
